import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { formatPersonFullName } from "../utils/formatters";
import type { Nurse } from "../utils/models";
import { departmentRosterQueryKey } from "./nursesQueryKeys";
import { useLastNameFirst } from "./useNameFormat";

// Reads and patches of the cached department roster that don't subscribe to it. A module of its own, apart from
// useRosterLookup, so HospitalDataProvider and save hooks can use it without importing the query hooks (and the
// analytics they load).

/**
 * Put a nurse the client just changed (time off added or removed) into the cached roster, so pages reading it show
 * the change without refetching the whole roster. Does nothing when the roster isn't cached — the next load is current.
 */
export const usePatchRosterNurse = (departmentId: string) => {
	const queryClient = useQueryClient();
	return useCallback(
		(updatedNurse: Nurse) =>
			queryClient.setQueryData<Nurse[]>(departmentRosterQueryKey(departmentId), (roster) =>
				roster?.map((nurse) => (nurse.id === updatedNurse.id ? updatedNurse : nurse)),
			),
		[queryClient, departmentId],
	);
};

/**
 * A roster nurse read from the cache when called, without subscribing to the roster — for save handlers and
 * success messages, where re-rendering on every roster change buys nothing. Undefined when the nurse isn't in the
 * cached roster.
 */
export const useReadRosterNurse = () => {
	const queryClient = useQueryClient();
	return useCallback(
		(departmentId: string, nurseId: number): Nurse | undefined =>
			queryClient.getQueryData<Nurse[]>(departmentRosterQueryKey(departmentId))?.find((candidate) => candidate.id === nurseId),
		[queryClient],
	);
};

/** A nurse's name from the cached roster when called (see `useReadRosterNurse`); undefined shows the unnamed message. */
export const useRosterNurseName = () => {
	const readRosterNurse = useReadRosterNurse();
	const lastNameFirst = useLastNameFirst();
	return useCallback(
		(departmentId: string, nurseId: number): string | undefined => {
			const nurse = readRosterNurse(departmentId, nurseId);
			return nurse ? formatPersonFullName(nurse, lastNameFirst) : undefined;
		},
		[readRosterNurse, lastNameFirst],
	);
};
